import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTournamentBracketTab } from './useTournamentBracketTab';

function BracketSideSourceField({
  sourceModes,
  side,
  label,
  source,
  onSourceChange,
  teamId,
  onTeamChange,
  teams,
  excludeTeamId,
  posId,
  onPosChange,
  groupPositions,
  formatGroupPositionLabel,
  crossPos,
  onCrossPosChange,
  crossIdx,
  onCrossIdxChange,
  formatCrossGroupRankLabel,
  idPrefix,
}) {
  const previewLabel =
    source === sourceModes.CROSS && crossPos && crossIdx
      ? formatCrossGroupRankLabel(crossPos, crossIdx)
      : '';

  return (
    <Stack spacing={1.25} sx={{ flex: 1 }}>
      <FormControl fullWidth size="medium">
        <InputLabel id={`${idPrefix}-source-${side}`} shrink>
          Origen ({label})
        </InputLabel>
        <Select
          labelId={`${idPrefix}-source-${side}`}
          label={`Origen (${label})`}
          value={source}
          onChange={(e) => onSourceChange(e.target.value)}
        >
          <MenuItem value={sourceModes.TEAM}>Equipo fijo</MenuItem>
          <MenuItem value={sourceModes.POSITION}>Plaza de grupo</MenuItem>
          <MenuItem value={sourceModes.CROSS}>Mejor clasificado entre grupos</MenuItem>
        </Select>
      </FormControl>

      {source === sourceModes.TEAM && (
        <FormControl fullWidth size="medium">
          <InputLabel id={`${idPrefix}-team-${side}`} shrink>
            {label} (equipo)
          </InputLabel>
          <Select
            labelId={`${idPrefix}-team-${side}`}
            label={`${label} (equipo)`}
            value={teamId}
            displayEmpty
            renderValue={(v) => (v ? teams.find((t) => t.id === v)?.name ?? v : 'Sin equipo fijo')}
            onChange={(e) => onTeamChange(e.target.value)}
          >
            <MenuItem value="">
              <em>Sin equipo fijo</em>
            </MenuItem>
            {teams
              .filter((t) => !excludeTeamId || t.id !== excludeTeamId)
              .map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}

      {source === sourceModes.POSITION && (
        <FormControl fullWidth size="medium">
          <InputLabel id={`${idPrefix}-pos-${side}`} shrink>
            Plaza {label.toLowerCase()}
          </InputLabel>
          <Select
            labelId={`${idPrefix}-pos-${side}`}
            label={`Plaza ${label.toLowerCase()}`}
            value={posId}
            displayEmpty
            renderValue={(v) => {
              if (!v) return 'Ninguna';
              const p = groupPositions.find((gp) => gp.id === v);
              return p ? formatGroupPositionLabel(p) : v;
            }}
            onChange={(e) => onPosChange(e.target.value)}
          >
            <MenuItem value="">
              <em>Ninguna</em>
            </MenuItem>
            {groupPositions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {formatGroupPositionLabel(p)}
              </MenuItem>
            ))}
          </Select>
          {groupPositions.length === 0 && (
            <FormHelperText>No hay plazas de grupo cargadas para este torneo.</FormHelperText>
          )}
        </FormControl>
      )}

      {source === sourceModes.CROSS && (
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Posición en grupo"
            type="number"
            size="medium"
            value={crossPos}
            onChange={(e) => onCrossPosChange(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            helperText="2 = 2º clasificado en cada grupo"
            fullWidth
          />
          <TextField
            label="Mejor #"
            type="number"
            size="medium"
            value={crossIdx}
            onChange={(e) => onCrossIdxChange(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            helperText={previewLabel || '1 = el mejor entre grupos'}
            fullWidth
          />
        </Stack>
      )}
    </Stack>
  );
}

function FeederDestinationPlacementField({ slots, value, onChange, idSuffix }) {
  if (!slots) return null;
  if (slots.freeform) {
    return (
      <TextField
        label="Orden en el destino"
        type="number"
        size="medium"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ min: 1, step: 1 }}
        helperText="No hay plaza libre con orden 1 u 2; indica un entero ≥ 1 que no repita otro enlace al mismo destino."
        fullWidth
        required
      />
    );
  }
  if (!slots.allowOrder1 && !slots.allowOrder2) return null;

  const coerced = value === '1' || value === '2' ? value : slots.allowOrder1 ? '1' : '2';
  let helper =
    'El ganador ocupará la plaza elegida en el partido destino (se envía al servidor como orden 1 = local, 2 = visitante).';
  if (slots.allowOrder1 && !slots.allowOrder2) {
    helper = 'La plaza visitante (orden 2) ya está ocupada por otro partido; este enlace ocupará la plaza local (orden 1).';
  } else if (!slots.allowOrder1 && slots.allowOrder2) {
    helper = 'La plaza local (orden 1) ya está ocupada por otro partido; este enlace ocupará la plaza visitante (orden 2).';
  }

  return (
    <FormControl fullWidth required size="medium">
      <InputLabel id={`feeder-side-${idSuffix}`}>Plaza del ganador en el destino</InputLabel>
      <Select
        labelId={`feeder-side-${idSuffix}`}
        label="Plaza del ganador en el destino"
        value={coerced}
        onChange={(e) => onChange(e.target.value)}
      >
        {slots.allowOrder1 ? <MenuItem value="1">Local (orden 1)</MenuItem> : null}
        {slots.allowOrder2 ? <MenuItem value="2">Visitante (orden 2)</MenuItem> : null}
      </Select>
      <FormHelperText>{helper}</FormHelperText>
    </FormControl>
  );
}

export default function TournamentSetupBracketTab(props) {
  const x = useTournamentBracketTab(props);

  // Estado de enlace de cada partido, para guiar el "Modo conectar".
  const matchLinkSummary = (m) =>
    m.nextMatchId ? `→ ${x.bracketNextLabel(m.nextMatchId)}` : 'sin enlazar';
  const unlinkedMatches = x.sortedBracketMatches.filter((m) => !m.nextMatchId);

  return (
    <>
      {x.crossGroupTies.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.65, fontWeight: 700, mb: 0.5 }}>
            Hay {x.crossGroupTies.length === 1 ? '1 empate' : `${x.crossGroupTies.length} empates`} en pools de
            «mejor entre grupos» que afectan al cuadro y no pueden resolverse automáticamente.
          </Typography>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.55, mb: 1 }}>
            Elige manualmente el orden de los equipos empatados; el sistema lo recuerda y la fase final se actualiza.
          </Typography>
          <Stack spacing={0.75}>
            {x.crossGroupTies.map((tie, i) => (
              <Stack
                key={`${tie.positionNumber}-${i}`}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                gap={1}
              >
                <Typography variant="body2">
                  <strong>{tie.positionLabel}</strong> — empate entre{' '}
                  {tie.tiedTeams.map((t, j) => (
                    <span key={t.teamId}>
                      {j > 0 ? ', ' : ''}
                      <strong>{t.teamName}</strong> ({t.groupName})
                    </span>
                  ))}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => x.openTieDialog(tie)}
                  disabled={x.busy}
                  sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#c9a227', color: '#1a1a1a', '&:hover': { bgcolor: '#b89220' } }}
                >
                  Resolver
                </Button>
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
        <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
          <strong>Manual:</strong> crea partidos con nombre, fecha, campo, equipos o <strong>plazas de grupo</strong>.
          Si enlazas a un <strong>siguiente partido</strong>, eliges <strong>local o visitante</strong> en el destino (el
          sistema guarda orden <strong>1</strong> o <strong>2</strong>; único por destino). <strong>Modo conectar</strong>{' '}
          usa la misma elección. <strong>Editar</strong> en cada fila para cambiar datos o enlace. <strong>Automático:</strong>{' '}
          genera el
          árbol vacío (4 / 8 / 16) ya numerado.
        </Typography>
      </Alert>

      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 1.5 }}>
            Añadir partido de eliminatoria
          </Typography>
          <Box
            component="form"
            onSubmit={x.handleAddBracketMatch}
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2.5,
              border: '1px solid rgba(15,53,49,0.14)',
              bgcolor: '#fafcf9',
              boxShadow: '0 2px 12px rgba(15,53,49,0.06)',
            }}
          >
            <Stack spacing={2}>
              <TextField
                label="Nombre del partido"
                size="medium"
                value={x.koMatchName}
                onChange={(e) => x.setKoMatchName(e.target.value)}
                placeholder="Ej. Semifinal 1"
                fullWidth
                required
              />
              <TextField
                label="Fecha y hora"
                type="datetime-local"
                size="medium"
                value={x.koMatchDayHour}
                onChange={(e) => x.setKoMatchDayHour(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Campo / pista (opcional)"
                size="medium"
                value={x.koFieldName}
                onChange={(e) => x.setKoFieldName(e.target.value)}
                placeholder="Ej. CAMPO 1"
                fullWidth
              />
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Origen de cada plaza (equipo fijo, plaza de grupo o «mejor entre grupos»)
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <BracketSideSourceField
                  sourceModes={x.BRACKET_SIDE_SOURCES}
                  side="local"
                  label="Local"
                  source={x.koLocalSource}
                  onSourceChange={x.setKoLocalSource}
                  teamId={x.koLocalTeamId}
                  onTeamChange={(v) => {
                    x.setKoLocalTeamId(v);
                    if (v && v === x.koForeignTeamId) x.setKoForeignTeamId('');
                  }}
                  teams={x.teams}
                  posId={x.koPosLocalId}
                  onPosChange={x.setKoPosLocalId}
                  groupPositions={x.groupPositions}
                  formatGroupPositionLabel={x.formatGroupPositionLabel}
                  crossPos={x.koCrossPosLocal}
                  onCrossPosChange={x.setKoCrossPosLocal}
                  crossIdx={x.koCrossIdxLocal}
                  onCrossIdxChange={x.setKoCrossIdxLocal}
                  formatCrossGroupRankLabel={x.formatCrossGroupRankLabel}
                  idPrefix="ko"
                />
                <BracketSideSourceField
                  sourceModes={x.BRACKET_SIDE_SOURCES}
                  side="foreign"
                  label="Visitante"
                  source={x.koForeignSource}
                  onSourceChange={x.setKoForeignSource}
                  teamId={x.koForeignTeamId}
                  onTeamChange={x.setKoForeignTeamId}
                  teams={x.teams}
                  excludeTeamId={x.koLocalTeamId}
                  posId={x.koPosForeignId}
                  onPosChange={x.setKoPosForeignId}
                  groupPositions={x.groupPositions}
                  formatGroupPositionLabel={x.formatGroupPositionLabel}
                  crossPos={x.koCrossPosForeign}
                  onCrossPosChange={x.setKoCrossPosForeign}
                  crossIdx={x.koCrossIdxForeign}
                  onCrossIdxChange={x.setKoCrossIdxForeign}
                  formatCrossGroupRankLabel={x.formatCrossGroupRankLabel}
                  idPrefix="ko"
                />
              </Stack>
              <FormControl fullWidth size="medium">
                <InputLabel id="ko-next" shrink>
                  Siguiente partido (encadenar cuadro)
                </InputLabel>
                <Select
                  labelId="ko-next"
                  label="Siguiente partido (encadenar cuadro)"
                  value={x.koNextMatchId}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return 'Ninguno (ej. final o partido suelto)';
                    return x.bracketNextLabel(v);
                  }}
                  onChange={(e) => x.handleKoNextMatchIdChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {x.sortedBracketMatches.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        (
                        {new Date(m.dayHour).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        )
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {Boolean(x.koNextMatchId) && (
                <FeederDestinationPlacementField
                  slots={x.createDestinationSlots}
                  value={x.koFeederOrder}
                  onChange={x.setKoFeederOrder}
                  idSuffix="create"
                />
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={x.busy || !x.koMatchName.trim()}
                fullWidth
                sx={{
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: '#0f6b5c',
                  '&:hover': { bgcolor: '#0c5a4e' },
                }}
              >
                Crear partido de eliminatoria
              </Button>
            </Stack>
          </Box>
        </Box>

        {x.sortedBracketMatches.length >= 2 && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: '1px dashed rgba(201,162,39,0.55)',
              bgcolor: 'rgba(201,162,39,0.06)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 0.5 }}>
              Modo conectar — enlaza el cuadro a mano
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
              Puedes crear todos los partidos en el orden que quieras (sin elegir «siguiente partido») y enlazarlos
              después aquí. Elige el <strong>origen</strong> (el partido cuyo ganador avanza), el <strong>destino</strong>{' '}
              y la <strong>plaza</strong> del ganador (local / visitante). Para rehacer un enlace, vuelve a conectar el
              origen o usa «Quitar enlace del origen».
            </Typography>
            {unlinkedMatches.length > 0 ? (
              <Typography
                variant="body2"
                sx={{ mb: 2, fontWeight: 700, color: '#0f6b5c', display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#d4a012',
                  }}
                />
                {unlinkedMatches.length === 1
                  ? '1 partido sin enlazar con su siguiente ronda (la final no necesita enlace).'
                  : `${unlinkedMatches.length} partidos sin enlazar con su siguiente ronda (la final no necesita enlace).`}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 700, color: '#0f6b5c' }}>
                Todos los partidos están enlazados con su siguiente ronda.
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="connect-from-label" shrink>
                  Origen (ganador pasa a…)
                </InputLabel>
                <Select
                  labelId="connect-from-label"
                  label="Origen (ganador pasa a…)"
                  value={x.connectFromId}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return 'Elige partido…';
                    const m = x.bracketMatches.find((bm) => bm.id === v);
                    return m?.name || v;
                  }}
                  onChange={(e) => x.setConnectFromId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Elige partido…</em>
                  </MenuItem>
                  {x.sortedBracketMatches.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 0.75,
                          fontWeight: m.nextMatchId ? 400 : 700,
                          color: m.nextMatchId ? 'text.secondary' : '#a67c00',
                        }}
                      >
                        · {matchLinkSummary(m)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="medium">
                <InputLabel id="connect-to-label" shrink>
                  Destino
                </InputLabel>
                <Select
                  labelId="connect-to-label"
                  label="Destino"
                  value={x.connectToId}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return 'Elige partido…';
                    const m = x.bracketMatches.find((bm) => bm.id === v);
                    return m?.name || v;
                  }}
                  onChange={(e) => x.setConnectToId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Elige partido…</em>
                  </MenuItem>
                  {x.sortedBracketMatches.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {Boolean(x.connectToId) ? (
                <Box sx={{ minWidth: { sm: 260 }, flex: { sm: '1 1 220px' } }}>
                  <FeederDestinationPlacementField
                    slots={x.connectDestinationSlots}
                    value={x.connectFeederOrder}
                    onChange={x.setConnectFeederOrder}
                    idSuffix="connect"
                  />
                </Box>
              ) : null}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={x.busy || !x.connectFromId || !x.connectToId}
                onClick={x.handleConnectBracketMatches}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: '#c9a227',
                  color: '#1a1a1a',
                  '&:hover': { bgcolor: '#b89220' },
                }}
              >
                Conectar origen → destino
              </Button>
              <Button
                variant="outlined"
                disabled={x.busy || !x.connectFromId}
                onClick={x.handleDisconnectBracketLink}
                sx={{ textTransform: 'none', fontWeight: 700, borderColor: 'rgba(15,53,49,0.35)', color: '#0f3531' }}
              >
                Quitar enlace del origen
              </Button>
            </Stack>
          </Box>
        )}

        {x.sortedBracketMatches.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 1.5 }}>
              Partidos de la fase final ({x.sortedBracketMatches.length})
            </Typography>
            <Stack spacing={2.5}>
              {x.bracketRounds.map((round) => (
                <Box key={round.key}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, color: '#0f6b5c', letterSpacing: '0.06em' }}
                  >
                    {round.title} ({round.matches.length})
                  </Typography>
                  <Stack spacing={1.25} sx={{ mt: 0.5 }}>
                    {round.matches.map((m) => {
                const when = new Date(m.dayHour);
                const whenStr = Number.isNaN(when.getTime())
                  ? String(m.dayHour)
                  : when.toLocaleString('es-ES', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                const localPos =
                  m.groupPositionLocal ?? x.groupPositions.find((p) => p.id === m.groupPositionLocalId);
                const foreignPos =
                  m.groupPositionForeign ?? x.groupPositions.find((p) => p.id === m.groupPositionForeignId);
                const localCrossLabel = x.formatCrossGroupRankLabel(
                  m.crossGroupRankPositionLocal,
                  m.crossGroupRankIndexLocal
                );
                const foreignCrossLabel = x.formatCrossGroupRankLabel(
                  m.crossGroupRankPositionForeign,
                  m.crossGroupRankIndexForeign
                );
                const localLabel =
                  m.localTeam?.name ||
                  m.groupPositionLocal?.team?.name ||
                  localPos?.team?.name ||
                  (localPos ? x.formatGroupPositionLabel(localPos) : null) ||
                  localCrossLabel ||
                  'Por definir';
                const foreignLabel =
                  m.foreignTeam?.name ||
                  m.groupPositionForeign?.team?.name ||
                  foreignPos?.team?.name ||
                  (foreignPos ? x.formatGroupPositionLabel(foreignPos) : null) ||
                  foreignCrossLabel ||
                  'Por definir';
                const feeders = [...(x.incomingBracketFeeds.get(m.id) || [])].sort(
                  (a, b) => (Number(a.order) || 1e9) - (Number(b.order) || 1e9)
                );
                const feedersStr = feeders.length
                  ? feeders.map((f) => `${f.name}${f.order != null ? ` (orden ${f.order})` : ''}`).join(', ')
                  : null;
                return (
                  <Paper
                    key={m.id}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: 'rgba(15,53,49,0.12)',
                      bgcolor: '#fff',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                      gap={1.5}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f3531' }}>
                          {m.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {localLabel}{' '}
                          <Typography component="span" variant="body2" sx={{ mx: 0.25 }}>
                            vs
                          </Typography>{' '}
                          {foreignLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {whenStr}
                          {m.fieldName ? ` · ${m.fieldName}` : ''}
                          {m.nextMatchId
                            ? ` · → ${x.bracketNextLabel(m.nextMatchId)}${
                                m.order != null ? ` · orden ${m.order} (1=local del destino)` : ''
                              }`
                            : ''}
                        </Typography>
                        {feedersStr ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            Recibe ganadores de: <strong>{feedersStr}</strong>
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.5} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                        <IconButton
                          size="small"
                          aria-label="Editar partido de eliminatoria"
                          onClick={() => x.openBracketEditDialog(m)}
                          disabled={x.busy}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Eliminar partido de eliminatoria"
                          onClick={() => x.handleDeleteBracketMatch(m.id)}
                          disabled={x.busy}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f3531', mb: 1 }}>
            Generación automática del cuadro vacío
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crea todos los partidos de la eliminatoria sin equipos, ya enlazados entre sí. Luego puedes editarlos en la
            vista de fase final o borrar y usar solo partidos manuales.
          </Typography>
          <Stack spacing={2} maxWidth={440}>
            <FormControl size="small" fullWidth>
              <InputLabel>Clasificados a la fase final</InputLabel>
              <Select
                value={x.koSize}
                label="Clasificados a la fase final"
                onChange={(e) => x.setKoSize(Number(e.target.value))}
              >
                <MenuItem value={4}>4 (2 semifinales + final)</MenuItem>
                <MenuItem value={8}>8 (4 cuartos + 2 semis + final)</MenuItem>
                <MenuItem value={16}>16 (8 octavos + … + final)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Inicio primera ronda"
              type="datetime-local"
              size="small"
              value={x.koStart}
              onChange={(e) => x.setKoStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Horas entre rondas"
              type="number"
              size="small"
              value={x.koHoursStep}
              onChange={(e) => x.setKoHoursStep(Number(e.target.value) || 24)}
              inputProps={{ min: 1, max: 168 }}
              fullWidth
            />
            <Button
              variant="contained"
              disabled={x.busy}
              onClick={x.handleSeedKnockout}
              className="!bg-btn-gold !text-ink !font-bold"
            >
              Crear cuadro eliminatorio automático
            </Button>
          </Stack>
        </Box>

        <Dialog open={x.bracketEditOpen} onClose={x.closeBracketEditDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800, color: '#0f3531' }}>Editar partido de eliminatoria</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Nombre del partido"
                size="medium"
                value={x.beName}
                onChange={(e) => x.setBeName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Fecha y hora"
                type="datetime-local"
                size="medium"
                value={x.beDayHour}
                onChange={(e) => x.setBeDayHour(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Campo / pista (opcional)"
                size="medium"
                value={x.beFieldName}
                onChange={(e) => x.setBeFieldName(e.target.value)}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <BracketSideSourceField
                  sourceModes={x.BRACKET_SIDE_SOURCES}
                  side="local"
                  label="Local"
                  source={x.beLocalSource}
                  onSourceChange={x.setBeLocalSource}
                  teamId={x.beLocalTeamId}
                  onTeamChange={(v) => {
                    x.setBeLocalTeamId(v);
                    if (v && v === x.beForeignTeamId) x.setBeForeignTeamId('');
                  }}
                  teams={x.teams}
                  posId={x.bePosLocalId}
                  onPosChange={x.setBePosLocalId}
                  groupPositions={x.groupPositions}
                  formatGroupPositionLabel={x.formatGroupPositionLabel}
                  crossPos={x.beCrossPosLocal}
                  onCrossPosChange={x.setBeCrossPosLocal}
                  crossIdx={x.beCrossIdxLocal}
                  onCrossIdxChange={x.setBeCrossIdxLocal}
                  formatCrossGroupRankLabel={x.formatCrossGroupRankLabel}
                  idPrefix="be"
                />
                <BracketSideSourceField
                  sourceModes={x.BRACKET_SIDE_SOURCES}
                  side="foreign"
                  label="Visitante"
                  source={x.beForeignSource}
                  onSourceChange={x.setBeForeignSource}
                  teamId={x.beForeignTeamId}
                  onTeamChange={x.setBeForeignTeamId}
                  teams={x.teams}
                  excludeTeamId={x.beLocalTeamId}
                  posId={x.bePosForeignId}
                  onPosChange={x.setBePosForeignId}
                  groupPositions={x.groupPositions}
                  formatGroupPositionLabel={x.formatGroupPositionLabel}
                  crossPos={x.beCrossPosForeign}
                  onCrossPosChange={x.setBeCrossPosForeign}
                  crossIdx={x.beCrossIdxForeign}
                  onCrossIdxChange={x.setBeCrossIdxForeign}
                  formatCrossGroupRankLabel={x.formatCrossGroupRankLabel}
                  idPrefix="be"
                />
              </Stack>
              <FormControl fullWidth size="medium">
                <InputLabel id="be-next" shrink>
                  Siguiente partido (ganador pasa a)
                </InputLabel>
                <Select
                  labelId="be-next"
                  label="Siguiente partido (ganador pasa a)"
                  value={x.beNextMatchId}
                  displayEmpty
                  renderValue={(v) => {
                    if (!v) return 'Ninguno';
                    return x.bracketNextLabel(v);
                  }}
                  onChange={(e) => x.handleBeNextMatchIdChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {x.sortedBracketMatches
                    .filter((bm) => !x.bracketEditBase || bm.id !== x.bracketEditBase.id)
                    .map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {Boolean(x.beNextMatchId) && (
                <FeederDestinationPlacementField
                  slots={x.editDestinationSlots}
                  value={x.beOrder}
                  onChange={x.setBeOrder}
                  idSuffix="edit"
                />
              )}
              {x.bracketEditBase?.completed ? (
                <Typography variant="caption" color="text.secondary">
                  Resultado y ganador se gestionan desde la mesa o el visor; aquí solo cambias calendario, equipos y
                  enlaces.
                </Typography>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={x.closeBracketEditDialog} disabled={x.busy} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={x.handleSaveBracketEdit}
              disabled={x.busy || !x.beName.trim()}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#0f6b5c', '&:hover': { bgcolor: '#0c5a4e' } }}
            >
              Guardar cambios
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={x.tieDialogOpen} onClose={x.closeTieDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 800, color: '#0f3531' }}>
            Resolver empate {x.tieDialogTie ? `· ${x.tieDialogTie.positionLabel}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ordena los equipos empatados de mejor (arriba) a peor. El orden se guarda y se aplica al cuadro inmediatamente.
            </Typography>
            <Stack spacing={1}>
              {x.tieDialogOrder.map((teamId, idx) => {
                const tieMember = x.tieDialogTie?.tiedTeams.find((t) => t.teamId === teamId);
                if (!tieMember) return null;
                return (
                  <Paper
                    key={teamId}
                    variant="outlined"
                    sx={{ px: 1.5, py: 1, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f3531', minWidth: 28 }}>
                      {idx + 1}º
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{tieMember.teamName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tieMember.groupName} · {tieMember.points} pts · GD {tieMember.goalDifference >= 0 ? '+' : ''}
                        {tieMember.goalDifference} · {tieMember.goalsFor} gf
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      disabled={idx === 0 || x.busy}
                      onClick={() => x.moveTieEntry(teamId, 'up')}
                      aria-label="Subir"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={idx === x.tieDialogOrder.length - 1 || x.busy}
                      onClick={() => x.moveTieEntry(teamId, 'down')}
                      aria-label="Bajar"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={x.closeTieDialog} disabled={x.busy} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={x.saveTieOrder}
              disabled={x.busy}
              sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#0f6b5c', '&:hover': { bgcolor: '#0c5a4e' } }}
            >
              Guardar orden
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </>
  );
}
